import { Box, Flex, Text, IconButton, Menu, MenuButton, MenuList, MenuItem, Tooltip, useColorMode } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box as BoxIcon,
    Sun,
    Crop,
    Unlock,
    Lock,
    Glasses,
    Lightbulb,
    LayoutTemplate,
    Square,
    EyeOff,
    Scan,
} from 'lucide-react';
import { useBIMStore } from '../state/useBIMStore';
import { useAppStore } from '../state/store';
import { useSketchStore } from '../state/useSketchStore';
import { useMode } from '../state';
import { annotationScaleManager } from '../core/services';

/**
 * @param embedded When true, renders as a static inline row for hosting
 * inside the StatusBar instead of floating over the viewport.
 */
export function ViewControlBar({ embedded = false }: { embedded?: boolean }) {
    // Selector-based subscriptions (whole-store destructuring re-renders this
    // bar on every element/preview change).
    const mode = useMode();
    const isBim = mode === 'bim';
    const isSketch = mode === 'sketch';
    const activeViewId = useBIMStore((s) => s.project.activeViewId);
    const activeViewScale = useBIMStore((s) => s.project.views.get(s.project.activeViewId)?.scale);
    const activeViewDetailLevel = useBIMStore((s) => s.project.views.get(s.project.activeViewId)?.detailLevel);
    const updateBimView = useBIMStore((s) => s.updateView);
    const view = useAppStore((s) => s.view);
    const updateView = useAppStore((s) => s.updateView);
    const toggleViewFlag = useAppStore((s) => s.toggleViewFlag);
    const cycleHideIsolate = useAppStore((s) => s.cycleHideIsolate);
    const sketchShadowsEnabled = useSketchStore((s) => s.shadows.enabled);
    const setSketchShadows = useSketchStore((s) => s.setShadows);
    const { colorMode } = useColorMode();
    const { t } = useTranslation();
    const isDark = colorMode === 'dark';

    // Single source of truth for the scale list: standard BIM scales merged
    // with the active view's own (possibly custom) scale so it stays visible.
    const scaleOptions = useMemo(() => {
        const current = activeViewScale ?? 100;
        return Array.from(new Set([...annotationScaleManager.getStandardScales(), current]))
            .filter((s) => s >= 1)
            .sort((a, b) => a - b);
    }, [activeViewScale]);

    /** Persist scale/detail changes onto the active BIM view. */
    const setViewScale = (scale: number) => {
        if (activeViewId) updateBimView(activeViewId, { scale });
    };
    const setDetailLevel = (level: 'Coarse' | 'Medium' | 'Fine') => {
        if (activeViewId) updateBimView(activeViewId, { detailLevel: level });
    };

    const iconColor = isDark ? 'white' : 'black';
    const bg = isDark ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)';
    const borderColor = isDark ? '#555' : '#ccc';

    const ControlButton = ({ icon, label, onClick, isActive = false }: { icon: any, label: string, onClick?: () => void, isActive?: boolean }) => (
        <Tooltip label={label} hasArrow placement={embedded ? 'bottom' : 'top'}>
            <IconButton
                aria-label={label}
                icon={icon}
                size="xs"
                variant="ghost"
                color={isActive ? 'blue.400' : iconColor}
                _hover={{ bg: isDark ? 'whiteAlpha.200' : 'blackAlpha.100' }}
                onClick={onClick}
                minW="24px"
                h="24px"
            />
        </Tooltip>
    );

    return (
        <Flex
            position={embedded ? 'static' : 'absolute'}
            bottom={embedded ? undefined : '20px'}
            left={embedded ? undefined : '20px'}
            h={embedded ? '100%' : undefined}
            gap={2}
            bg={embedded ? 'transparent' : bg}
            p={embedded ? 0 : 1}
            borderRadius="sm"
            border={embedded ? 'none' : '1px solid'}
            borderColor={embedded ? undefined : borderColor}
            alignItems="center"
            zIndex={embedded ? undefined : 10}
        >
            {/* Scale + Detail Level — BIM view properties (no per-view scale
                or LOD concept on the other workbenches). */}
            {isBim && (
                <>
                    <Menu placement={embedded ? 'bottom' : 'top'}>
                        <Tooltip label={t('ui.viewControl.viewScale')} hasArrow placement={embedded ? 'bottom' : 'top'}>
                            <MenuButton as={Box} cursor="pointer" px={2} py={1} _hover={{ bg: isDark ? 'whiteAlpha.200' : 'blackAlpha.100' }} borderRadius="sm">
                                <Text fontSize="xs" fontWeight="bold">1:{activeViewScale || 100}</Text>
                            </MenuButton>
                        </Tooltip>
                        <MenuList minW="max-content" fontSize="xs">
                            {scaleOptions.map((s) => (
                                <MenuItem key={s} onClick={() => setViewScale(s)}>
                                    1:{s}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>

                    <Box w="1px" h="16px" bg={borderColor} />

                    {/* Detail Level */}
                    <Menu placement={embedded ? 'bottom' : 'top'}>
                        <Tooltip label={t('ui.viewControl.detailLevelTooltip', { level: t(`ui.viewControl.${(activeViewDetailLevel || 'coarse').toLowerCase()}` as any) })} hasArrow placement={embedded ? 'bottom' : 'top'}>
                            <MenuButton as={IconButton} aria-label={t('ui.viewControl.detailLevel')} icon={<BoxIcon size={14} />} size="xs" variant="ghost" />
                        </Tooltip>
                        <MenuList minW="100px" fontSize="xs">
                            <MenuItem onClick={() => setDetailLevel('Coarse')}>{t('ui.viewControl.coarse')}</MenuItem>
                            <MenuItem onClick={() => setDetailLevel('Medium')}>{t('ui.viewControl.medium')}</MenuItem>
                            <MenuItem onClick={() => setDetailLevel('Fine')}>{t('ui.viewControl.fine')}</MenuItem>
                        </MenuList>
                    </Menu>
                </>
            )}

            {/* Visual Style — every shaded workbench. Sketch owns its own
                display-style store: same five styles, per-mode writer. */}
            {mode !== '2d' && (
                <Menu placement={embedded ? 'bottom' : 'top'}>
                    <Tooltip label={t('ui.viewControl.visualStyle')} hasArrow placement={embedded ? 'bottom' : 'top'}>
                        <MenuButton as={IconButton} aria-label={t('ui.viewControl.visualStyle')} icon={<BoxIcon size={14} />} size="xs" variant="ghost" />
                    </Tooltip>
                    <MenuList minW="120px" fontSize="xs">
                        <MenuItem onClick={() => isSketch ? useSketchStore.getState().setDisplayStyle('wireframe') : updateView({ viewMode: 'wireframe' })}>{t('ui.viewControl.wireframe')}</MenuItem>
                        <MenuItem onClick={() => isSketch ? useSketchStore.getState().setDisplayStyle('hiddenLine') : updateView({ viewMode: 'hiddenLine' })}>{t('ui.viewControl.hiddenLine')}</MenuItem>
                        <MenuItem onClick={() => isSketch ? useSketchStore.getState().setDisplayStyle('shaded') : updateView({ viewMode: 'shaded' })}>{t('ui.viewControl.shaded')}</MenuItem>
                        <MenuItem onClick={() => isSketch ? useSketchStore.getState().setDisplayStyle('consistentColors') : updateView({ viewMode: 'consistentColors' })}>{t('ui.viewControl.consistentColors')}</MenuItem>
                        <MenuItem onClick={() => isSketch ? useSketchStore.getState().setDisplayStyle('realistic') : updateView({ viewMode: 'realistic' })}>{t('ui.viewControl.realistic')}</MenuItem>
                    </MenuList>
                </Menu>
            )}

            {/* Plan/3D + Projection — every orbiting workbench. Sketch's
                viewport branches on the same shared viewType/projection
                state (plan = fixed ortho top camera). */}
            {mode !== '2d' && (
                <>
                    <Box w="1px" h="16px" bg={borderColor} />

                    {(isBim || isSketch) && (
                        <ControlButton
                            icon={<LayoutTemplate size={14} />}
                            label={view.viewType === 'plan' ? t('ui.viewControl.switchTo3d') : t('ui.viewControl.switchToPlan')}
                            isActive={view.viewType === 'plan'}
                            onClick={() => updateView({ viewType: view.viewType === 'plan' ? '3d' : 'plan' })}
                        />
                    )}

                    {/* Orthographic/Perspective Toggle */}
                    <ControlButton
                        icon={view.projection === 'orthographic' ? <Square size={14} /> : <BoxIcon size={14} />}
                        label={view.projection === 'orthographic' ? t('ui.viewControl.switchToPerspective') : t('ui.viewControl.switchToOrthographic')}
                        isActive={view.projection === 'orthographic'}
                        onClick={() => updateView({ projection: view.projection === 'orthographic' ? 'perspective' : 'orthographic' })}
                    />
                </>
            )}

            {/* Sun Path + Shadows — sun studies exist on the BIM and Sketch
                workbenches; each toggles its own shadow state. */}
            {(isBim || isSketch) && (
                <>
                    <Box w="1px" h="16px" bg={borderColor} />

                    {isBim && (
                        <ControlButton
                            icon={<Sun size={14} />}
                            label={view.sunPathVisible ? `${t('ui.viewControl.sunPath')}: ${t('ui.viewControl.on')}` : `${t('ui.viewControl.sunPath')}: ${t('ui.viewControl.off')}`}
                            isActive={view.sunPathVisible}
                            onClick={() => toggleViewFlag('sunPathVisible')}
                        />
                    )}

                    {/* Shadows */}
                    <ControlButton
                        icon={<BoxIcon size={14} />}
                        label={isSketch
                            ? (sketchShadowsEnabled ? `${t('ui.viewControl.shadows')}: ${t('ui.viewControl.on')}` : `${t('ui.viewControl.shadows')}: ${t('ui.viewControl.off')}`)
                            : (view.shadowsEnabled ? `${t('ui.viewControl.shadows')}: ${t('ui.viewControl.on')}` : `${t('ui.viewControl.shadows')}: ${t('ui.viewControl.off')}`)}
                        isActive={isSketch ? sketchShadowsEnabled : view.shadowsEnabled}
                        onClick={() => isSketch
                            ? setSketchShadows({ enabled: !sketchShadowsEnabled })
                            : toggleViewFlag('shadowsEnabled')}
                    />
                </>
            )}

            {/* Crop / Lock / Hide-Isolate / Reveal — BIM view semantics
                (crop regions, view templates, element visibility). */}
            {isBim && (
                <>
                    <Box w="1px" h="16px" bg={borderColor} />

                    {/* Crop View */}
                    <ControlButton
                        icon={<Crop size={14} />}
                        label={view.cropView ? `${t('ui.viewControl.cropView')}: ${t('ui.viewControl.on')}` : `${t('ui.viewControl.cropView')}: ${t('ui.viewControl.off')}`}
                        isActive={view.cropView}
                        onClick={() => toggleViewFlag('cropView')}
                    />

                    {/* Show Crop Region */}
                    <ControlButton
                        icon={<Scan size={14} />}
                        label={view.showCropRegion ? `${t('ui.viewControl.showCropRegion')}: ${t('ui.viewControl.on')}` : `${t('ui.viewControl.showCropRegion')}: ${t('ui.viewControl.off')}`}
                        isActive={view.showCropRegion}
                        onClick={() => toggleViewFlag('showCropRegion')}
                    />

                    <Box w="1px" h="16px" bg={borderColor} />

                    {/* Unlock/Lock 3D View */}
                    <ControlButton
                        icon={view.viewLocked ? <Lock size={14} /> : <Unlock size={14} />}
                        label={view.viewLocked ? t('ui.viewControl.unlock3dView') : t('ui.viewControl.lock3dView')}
                        isActive={view.viewLocked}
                        onClick={() => toggleViewFlag('viewLocked')}
                    />

                    {/* Temporary Hide/Isolate (cycles: off -> isolate -> hide -> off) */}
                    <ControlButton
                        icon={<Glasses size={14} />}
                        label={
                            view.hideIsolateMode === 'isolate'
                                ? t('ui.viewControl.temporaryIsolateOn')
                                : view.hideIsolateMode === 'hide'
                                    ? t('ui.viewControl.temporaryHideOn')
                                    : t('ui.viewControl.temporaryHideIsolate')
                        }
                        isActive={view.hideIsolateMode !== null}
                        onClick={() => cycleHideIsolate()}
                    />

                    {/* Reveal Hidden Elements */}
                    <ControlButton
                        icon={view.revealHidden ? <EyeOff size={14} /> : <Lightbulb size={14} />}
                        label={view.revealHidden ? `${t('ui.viewControl.revealHidden')}: ${t('ui.viewControl.on')}` : t('ui.viewControl.revealHidden')}
                        isActive={view.revealHidden}
                        onClick={() => toggleViewFlag('revealHidden')}
                    />
                </>
            )}

        </Flex>
    );
}
